import React, {ReactElement, RefObject} from "react";
import {ChatBar, ChatBarConfig, ChatBarListener} from "./ChatBar";
import {Box, Divider} from "@mui/material";
import {PAGE_FRAGMENT_WIDTH, PD_LG, PD_XLG} from "@/shared/dimens";
import {ChatItem, ChatItemView, ChatViewContext, ChatViewListener} from "./ChatItemView";
import {ChatMessage, ChatMessages, ChatMessageType, ChatThreads, MessageFactory, ParticipantKey} from "./types";
import {Action, ACTION_SPACE, ActionBase, ActionToolbar, ListItemChange, OnListItemsListener} from "@/shared/types";
import {PageFragment, PageFragmentStyleFlags} from "@/shared/PageFragment";
import {InfoOutlined, LocalPhoneOutlined, MoreVertOutlined, VideoCallOutlined} from "@mui/icons-material";
import {BaseApp} from "@/shared/BaseApp";
import {AudioCallDialogFragment} from "../calls/AudioCallDialogFragment";
import {
  BaseChatThreadFragment,
  BaseChatThreadFragmentProps,
  BaseChatThreadFragmentState
} from "../BaseChatThreadFragment";
import {CallFragment, CallFragmentListener} from "../calls/CallFragment";
import {MessagingChatFeatures} from "../types";
import {CallChannel, CallLogs, CallType} from "../calls/types";

export type ChatThreadFragmentProps = BaseChatThreadFragmentProps & {
  showBack?: boolean,
  toolbarActions?: ActionBase[],
  features?: MessagingChatFeatures,
  myParticipantKey: ParticipantKey,
  chatBarConfig?: ChatBarConfig,
  chatBarElement?: ReactElement,
  chatBarStatus?: ReactElement,
  onBackPressed?: () => boolean,
  onThreadDeleted?: () => void,
}

export type ChatThreadFragmentPrivateProps = {
  onToggleInfoButtonClicked?: () => void,
}

type ChatThreadFragmentState = BaseChatThreadFragmentState & {
  items?: ChatItem[],
  messages: ChatMessage[],
}

export class ChatThreadFragment<P extends ChatThreadFragmentProps = ChatThreadFragmentProps> extends BaseChatThreadFragment<P & ChatThreadFragmentPrivateProps, ChatThreadFragmentState>
  implements OnListItemsListener<ChatMessage>, ChatViewListener, ChatBarListener, CallFragmentListener {

  private readonly chatContainer: RefObject<any> = React.createRef();

  private messages: ChatMessages;
  private lastUserGenMessage: ChatMessage;

  protected onCreateState(): ChatThreadFragmentState {
    return {
      ...super.onCreateState(),
      messages: [],
    };
  }

  componentDidMount() {
    // We need to add this here, as sometimes we get the mount -> unmount -> mount sequence, so we need to re-register
    // with messages.
    this.messages?.registerObserver(this);
    super.componentDidMount();
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    this.messages?.unregisterObserver(this);
  }

  componentDidUpdate(prevProps: Readonly<ChatThreadFragmentProps>, prevState: Readonly<ChatThreadFragmentState>, snapshot?: any) {
    if (this.state.messages.length !== prevState.messages.length) {
      this.scrollToBottom();
    }
    if (this.props.thread.id !== prevProps.thread.id) {
      this.reload();
    }
  }

  protected containerContentDidRender() {
    super.containerContentDidRender();
    this.scrollToBottom();
  }

  private scrollToBottom() {
    setTimeout(() => {
      const current = this.chatContainer.current;
      if (current) {
        const height = current.scrollHeight;
        current.scrollTo(0, height);
      }
    }, 1);
  }

  protected styleFlags(): number | PageFragmentStyleFlags {
    return PageFragment.STYLE_TOOLBAR_TYPE_FLAG
      | (this.props.showBack ? PageFragment.STYLE_BACK_BUTTON_FLAG : 0)
      | (this.props.features?.nestedChatView ? PageFragment.STYLE_BACK_BUTTON_FLAG : 0);
  }

  onBackButtonClicked() {
    if (this.props.onBackPressed?.()) {
      return;
    }
    super.onBackButtonClicked();
  }

  protected getToolbar(toolbarModeId: string | null): React.ReactElement {
    return this.renderToolbarButtonsInToolbarContainerEx([
      ACTION_SPACE,
      // new ActionToolbar("Conference Call", () => {
      //   this.props.path.navigate("/meetings/" + this.props.thread.id);
      // }, MeetingRoomOutlined),
      ...(this.props.toolbarActions || []),
      this.props.features?.hasAudioCall
        ? new ActionToolbar("Audio Call", () => {
          const thread = this.props.thread;
          const message = MessageFactory.createCallMessage(thread.id, thread.myParticipantKey(), CallType.CREATE, CallChannel.AUDIO);
          this.messages.addListItem(message)
            .then(() => {
              BaseApp.CONTEXT.showFloatingView(() =>
                <AudioCallDialogFragment
                  onClose={() => BaseApp.CONTEXT.hideFloatingView()}
                  path={this.props.path}
                  thread={this.props.thread}/>);
            });
        }, LocalPhoneOutlined)
        : null,
      this.props.features?.hasVideoCall
        ? new ActionToolbar("Video Call", () => {
          const thread = this.props.thread;
          const message = MessageFactory.createCallMessage(thread.id, thread.myParticipantKey(), CallType.CREATE, CallChannel.VIDEO);
          this.messages.addListItem(message)
            .then(() => {
              BaseApp.CONTEXT.showFullscreenDialog(() => <CallFragment thread={this.props.thread} message={message}
                                                                       listener={this}/>);
            });
        }, VideoCallOutlined)
        : null,
      this.props.features?.hasInfo
        ? new ActionToolbar("", () => {
          this.props.onToggleInfoButtonClicked?.();
        }, InfoOutlined)
        : null,
      new ActionToolbar("Menu", () => {
        BaseApp.CONTEXT.showActionsDialog("Actions", [
          new Action("Delete thread", () => {
            const frag = this;
            const messages = this.messages;
            const threads = ChatThreads.getInstance(this.props.myParticipantKey.type);
            const thread = this.props.thread;
            BaseApp.CONTEXT.showConfirmDialog("Are you sure you want to delete this thread? This action cannot be undone.", () => {
              setTimeout(() => {
                BaseApp.CONTEXT.showProgressDialog("Pleas wait...", null, async function () {
                  await messages.deleteAllListItems();
                  await threads.deleteListItem(thread);
                  frag.props.onThreadDeleted?.();
                  BaseApp.CONTEXT.showToast("Thread deleted.");
                })
              }, 10);
            });
          }).makeDestructive(),
        ]);
      }, MoreVertOutlined)
    ].filter(Boolean));
  }

  onCallEnded() {
    BaseApp.CONTEXT.hideDialog();
  }

  onMinimizedStateChanged(minimized: boolean) {
  }

  protected async fetchOnMount(): Promise<void> {
    await super.fetchOnMount();
    if (this.messages) {
      this.messages.unregisterObserver(this);
    }
    this.messages = ChatMessages.getInstance(this.props.myParticipantKey.type, this.props.thread.id);
    this.messages.registerObserver(this);
    this.setState({
      title: this.props.thread.getDisplayName(),
      messages: [],
    });
    await this.messages.maybeLoadListItems();
    this.updateItems();
  }

  renderContent(): ReactElement {
    const items = this.state.items;
    return <Box
      style={{display: "flex", flexDirection: "column", height: 0, flexGrow: 1, overflow: "hidden"}}>
      <Box ref={this.chatContainer}
           className="hidescroll"
           style={{
             display: "flex",
             flexDirection: "column",
             width: '100%',
             margin: "auto",
             maxWidth: PAGE_FRAGMENT_WIDTH,
             overflowY: 'scroll',
             flexGrow: 1,
             padding: PD_LG,
             paddingBottom: PD_XLG,
             gap: PD_LG,
           }}>
        <span style={{marginBottom: 'auto'}}/>
        {items?.map((item, i) => (
          <ChatItemView
            color={this.props.features?.color}
            item={item}
            alignment={this.props.features?.alignment || (this.props.features?.nestedChatView ? "left" : "auto")}
            chatViewListener={this}
            myParticipantKey={this.props.thread.myParticipantKey()}/>
        ))}
      </Box>
      {this.props.chatBarStatus}
      <Divider/>
      <div style={{width: "100%", flexShrink: 0, position: "relative"}}>
        <div style={{
          width: "100%",
          opacity: this.props.chatBarElement ? 0 : 1,
        }}>
          <ChatBar config={this.props.chatBarConfig} listener={this}/>
        </div>
        {this.props.chatBarElement
          ? <div style={{
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
            position: "absolute",
          }}>
            {this.props.chatBarElement}
          </div>
          : null}
      </div>
    </Box>;
  }

  private checkLastMessageChanged(lastMessage: ChatMessage): boolean {
    if (this.lastUserGenMessage?.id === lastMessage.id) {
      return false;
    }
    this.lastUserGenMessage = lastMessage;
    return true;
  }

  onItemChanged(message: ChatMessage, change: ListItemChange) {
    switch (message.type) {
      case ChatMessageType.CALL: {
        const messageCallLog = message.callLog;
        if (messageCallLog) {
          const type = this.props.myParticipantKey.type;
          (async function () {
            const callLogs = CallLogs.getInstance(type);
            const callLog = await callLogs.getOrLoadItem(messageCallLog.id);
            if (!callLog || callLog?.updatedAt !== messageCallLog.updatedAt) {
              messageCallLog.message = message;
              await callLogs.addListItem(messageCallLog);
            }
          })();
        }
      }
        break;
    }

    this.updateItems();
  }

  private updateItems(): void {
    const messages = this.messages.getListItems();
    const items: ChatItem[] = [];
    let lastDay = 0;
    let lastYear = 0;
    let prevItem: ChatItem = null;
    let item: ChatItem;
    let lastUserGeneratedItem;
    for (let message of messages) {
      if (!message.isUserGenerated(false)) {
        continue;
      }
      let date = new Date(message.createdAt);
      let year = date.getFullYear();
      let day = date.getDay();
      if ((year !== lastYear) || (day !== lastDay)) {
        lastYear = year;
        lastDay = day;
        items.push(item = {
          prevMessage: prevItem?.message,
          message: MessageFactory.createMetaMessage(this.props.thread.id, message.createdAt - 1),
          viewContext: ChatViewContext.THREAD
        });
        if (prevItem) {
          prevItem.nextMessage = item.message;
        }
        prevItem = item;
      }
      items.push(item = {prevMessage: prevItem?.message, message: message, viewContext: ChatViewContext.THREAD});
      if (prevItem) {
        prevItem.nextMessage = item.message;
      }
      prevItem = item;
      lastUserGeneratedItem = item;
    }
    if (lastUserGeneratedItem) {
      if (this.checkLastMessageChanged(lastUserGeneratedItem.message)) {
        if (lastUserGeneratedItem.message.from?.id !== this.props.thread.myParticipantKey().id) {
          //this.typings.clearCachedTyping(lastUserGeneratedItem.message.from);
        } else {
          // scrollToBottom = true;
        }
      }
    }
    // if (otherTyping = this.typings.getCachedTyping(this.props.thread.other.uid)) {
    //   items.push({message: MessageFactory.createTypingMessage(otherTyping), viewContext: ChatViewContext.THREAD});
    // } else if (otherPresence = this.presences.getCachedPresence(this.props.thread.other.uid)) {
    //   items.push({
    //     message: MessageFactory.createPresenceMessage(otherPresence),
    //     viewContext: ChatViewContext.THREAD
    //   });
    // } else {
    //   items.push({
    //     message: MessageFactory.createPresenceMessage(new ChatPresence(this.props.thread.other.uid, "-", 0, ChatPresence.TYPE_HIDDEN)),
    //     viewContext: ChatViewContext.THREAD
    //   });
    // }
    this.setState({
      messages: messages,
      items: items,
    });
  }

  onChatViewOptionsClicked(message: ChatMessage): void {
    const actions: Action[] = [];
    if (message.from.id === this.props.thread.myParticipantKey().id) {
      actions.push(new Action("Delete", () => {
        BaseApp.CONTEXT.showConfirmDialog("Are you sure you want to delete this message? This action cannot be undone.", () => {
          this.deleteMessage(message);
        })
      }).makeDestructive());
    }
    if (!(actions.length > 0)) {
      return;
    }
    BaseApp.CONTEXT.showActionsDialog("Actions", actions);
  }

  private async deleteMessage(message: ChatMessage): Promise<void> {
    await this.messages.deleteListItem(message);
    BaseApp.CONTEXT.showToast("Message deleted.");
  }

  onSend(text: string) {
    const thread = this.props.thread;
    const message = MessageFactory.createTextMessage(thread.id, thread.myParticipantKey(), text);
    this.messages.addListItem(message);
  }

  onTyping() {
    // this.typing.setTyping(new ChatTyping(this.appPrefs.getUsername(), this.state.threadKey.id, this.props.other.uid, Date.now()));
  }

  onSendMeetingInvite(channel: string): void {
    // this.phonecallActionsHelper.invite(this.props.other.uid, this.state.threadKey, channel);
  }
}